/* eslint-disable react/prop-types */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Button from '../button/Button';
import { createPortal } from 'react-dom'
import Checkbox from '../checkbox/Checkbox';
import styles from './multi-value-input.module.scss'
import Modal from '../modal/Modal';
import { DispatchContext } from 'src/AppContext';
import ShowAreaCodes from '../modal/modelContent/showAreaCodes/ShowAreaCodes';

interface IProps {
  list: Array<any>
  modifiers?: string
  label: string
  name: string
  readOnly?: boolean
  error?: boolean
  areaCodeLabel: string
  disabled?: boolean
  selectedValues?: Array<any>
  resetError: () => void
  updateList: any
  placeholder: string
}

const AreaCodes = React.memo(function (props: IProps) {
  const [inputValue, setInputValue] = useState('')
  const [list, updateList] = useState(props.list)
  const [showModal, setShowModal] = useState(false)
  const [touched, setTouched] = useState(false)
  const [selectedValues, updateSelectedValues] = useState([])
  const dispatch = useContext(DispatchContext)
  const [focused, setFocused] = useState(false)
  const flattenList = useMemo(() => props.list.map(({ codes }) => codes).flat(), [props.list])
  const container = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (props.selectedValues) {
      updateSelectedValues(props.selectedValues)
    }
  }, [props.selectedValues])


  useEffect(() => {
    if (props.list) {
      updateList(props.list)
    }
  }, [props.list])

  useEffect(() => {
    const clickedOuSide = (e: any) => {
      if (container.current && !container.current.contains(e.target)) {
        setFocused(false)
      }
    }

    window.addEventListener('click', clickedOuSide)
    return () => window.removeEventListener('click', clickedOuSide)
  }, [])

  const handleChange = (value: string) => {
    if (value) {
      setTouched(true)
      setFocused(true)
      if (props.list.filter(v => v.navn.includes(value)).length) {
        updateList(props.list.filter(v => v.navn.toLowerCase().includes(value.toLowerCase())))
      } else {
        updateList(flattenList.filter(v => v.navn.toLowerCase().includes(value.toLowerCase()) || v.nr.includes(value)))
      }
    } else {
      setTouched(false)
      updateList(props.list)
    }

    setInputValue(value)
  }

  const toggleSingle = (value: any) => {
    if (!selectedValues.find(val => Number(value.nr) === Number(val))) {
      updateSelectedValues([...selectedValues, value.nr])
      props.updateList([...selectedValues, value.nr])
    } else {
      const values = [...selectedValues]
      const index = selectedValues.findIndex((val) => Number(val) === Number(value.nr))
      values.splice(index, 1)
      updateSelectedValues(values)
      props.updateList(values)
    }
  }

  const toggleCommune = (codes: any, checked: boolean) => {
    if (checked) {
      const arr = selectedValues.filter(val => !codes.find(code => Number(code.nr === val)));
      updateSelectedValues([...arr, ...codes.map(code => code.nr)])
      props.updateList([...arr, ...codes.map(code => code.nr)])
    } else {
      updateSelectedValues(selectedValues.filter(val => !codes.find(code => Number(code.nr) === Number(val))))
      props.updateList(selectedValues.filter(val => !codes.find(code => Number(code.nr) === Number(val))))
    }
  }

  const toggleAll = (checked) => {
    if (checked) {
      const arr = [];
      props.list.forEach(({ codes }) => {
        arr.push(...codes.map(code => code.nr))
      });
      updateSelectedValues(arr)
      props.updateList(arr)
    }
    else {
      updateSelectedValues([])
      props.updateList([])
    }
  }

  const updateLists = (list) => {
    updateSelectedValues(list)
    props.updateList(list)
  }

  const handleFocus = () => {
    setFocused(!focused)
    if (props.resetError) [
      props.resetError()
    ]
  }

  const renderModal = () => {
    return createPortal(
      <div className={styles.modal}>
        <ShowAreaCodes updateList={updateLists} areaCodes={selectedValues} title={props.areaCodeLabel} close={closeModal} isEditable={true} />
      </div>,
      document.querySelector('.modal')
    );
  }

  const closeModal = useCallback(() => {
    setShowModal(false)
    dispatch({ showModal: true, type: 'SHOW_MODAL', closeOnClick: true, modalType: 'addDiscountCode' })

  }, [dispatch])

  return (
    <>
      {showModal && renderModal()}
      <div className={`${styles.inputField} ${props.modifiers ? props.modifiers : ''}`}>
        {props.label && <label htmlFor={props.name} className={styles.inputLabel}>{props.label}</label>}
        {selectedValues.length > 0 && <button type='button' onClick={() => {
          setShowModal(true)
          dispatch({ showModal: true, type: 'SHOW_MODAL', closeOnClick: false, modalType: 'addDiscountCode' })
        }} className={styles.showAllButton}>
          <span>{selectedValues.length} {selectedValues.length === 1 ? 'valgt' : 'valgte'}</span> <svg xmlns="http://www.w3.org/2000/svg" className="tw-h-4 tw-w-4 tw-ml-1" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg></button>}
        <div ref={container} className={`${styles.inputWrapper} ${focused ? styles.focused : ''} ${props.error ? styles.errorState : ''}`}>
          <input
            id={props.name}
            onClick={(e) => handleFocus()}
            className={`${styles.input}`}
            name='areaCodes'
            placeholder={props.placeholder}
            type='text'
            disabled={props.disabled}
            autoComplete='off'
            readOnly={props.readOnly}
            value={inputValue || ''}
            onChange={(e: any) => handleChange(e.target.value)}
          />
          {focused && !props.disabled && <ul className={styles.list}>
            {list && list.length > 0 ?
              <>
                {!touched && <li className={`${styles.item}`}>
                  <Checkbox labelRight='Hele Danmark' modifiers='tw-w-full' name='select all' curried={false} handleChange={(checked) => toggleAll(checked)} checked={(function () {
                    return flattenList.filter(code => selectedValues.find(val => Number(val) === Number(code.nr))).length === flattenList.length
                  })()
                  }
                  />
                </li>}
                {list.map((suggestion, index) => {
                  return (
                    <React.Fragment key={index}>
                      <input id={`dropdown${index}`} type="checkbox" className={styles.checkbox} />
                      <label htmlFor={`dropdown${index}`}>
                        <li className={`${styles.item}`} key={index}>
                          <Checkbox
                            name={suggestion.navn}
                            curried={false}
                            labelRight={!suggestion.codes ? suggestion.navn : ''}
                            handleChange={(checked) => suggestion.codes ? toggleCommune(suggestion.codes, checked) : toggleSingle(suggestion)}
                            checked={suggestion.codes ? selectedValues.filter(val => suggestion.codes.find(code => Number(val) === Number(code.nr))).length === suggestion.codes.length : !!selectedValues.find(val => Number(val) === Number(suggestion.nr))}
                          />
                          {suggestion.codes && <span className='tw-ml-4 tw-text-sm'>{suggestion.navn}</span>}
                          {!suggestion.codes ?
                            <span className='tw-ml-auto tw-opacity-50 tw-text-sm'>{suggestion.nr}</span> :
                            <svg xmlns="http://www.w3.org/2000/svg" className="tw-h-5 tw-w-5 tw-ml-auto" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>}
                        </li>
                      </label>
                      {suggestion.codes && <li className={styles.expand}>
                        <ul>
                          {suggestion.codes.map((code, i) => {
                            return (
                              <li className={`${styles.item} ${styles.subItem}`} key={i}>
                                <Checkbox modifiers='tw-w-full' labelRight={code.navn} curried={false} handleChange={() => toggleSingle(code)} name={code.navn} checked={!!selectedValues.find(val => Number(val) === Number(code.nr))} />
                                <span className='tw-ml-auto tw-opacity-50 tw-text-sm'>{code.nr}</span>
                              </li>
                            )
                          })}
                        </ul>
                      </li>}
                    </React.Fragment>
                  )
                })}
              </>
              :
              <li className={styles.item}>Ingen resultater fundet</li>}
          </ul>}
          <span className={styles.dropDownIcon} onClick={(e) => {
            handleFocus()
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </span>
        </div>
      </div>
    </>
  )
})

export default AreaCodes
